import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";

export const NowPicking = ({
  sessionId,
}: {
  sessionId: string | undefined;
}) => {
  const [currentTeam, setCurrentTeam] = useState<any>(null);

  const fetchCurrentSelection = async () => {
    if (!sessionId) return;
    try {
      const { data: teams } = await supabase
        .from("teams")
        .select("*")
        .order("draft_position", { ascending: true });

      const { count } = await supabase
        .from("picks")
        .select("*", { count: "exact", head: true })
        .eq("session_id", sessionId);

      if (teams && teams.length > 0) {
        const totalPicks = count || 0;
        const teamIndex = totalPicks % teams.length;
        setCurrentTeam(teams[teamIndex]);
      }
    } catch (err) {
      console.error("Error syncing current pick:", err);
    }
  };

  useEffect(() => {
    fetchCurrentSelection();
    const channel = supabase
      .channel(`now-picking-sync-${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "picks",
          filter: `session_id=eq.${sessionId}`,
        },
        () => fetchCurrentSelection()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId]);

  if (!currentTeam) return null;

  const teamColor = currentTeam.primary_color || "#f9eeda";
  const empireGreen = "#22565a";

  const getLogoUrl = (teamName: string) => {
    if (!teamName)
      return "https://empireproleague.com/wp-content/uploads/2021/05/Empire-Logo-Official.png";
    const fileName = `${encodeURIComponent(teamName)}.png`;
    return `https://dpsrbqvcukoomuupjfdm.supabase.co/storage/v1/object/public/draft%20bucket/${fileName}`;
  };

  const displayLogo = getLogoUrl(currentTeam.team_name);

  return (
    <div className="empire-war-room-container">
      <div className="bg-static-texture"></div>

      <div className="bg-massive-logo-wrapper">
        <img
          src={displayLogo}
          alt="Team Logo Texture"
          key={displayLogo}
          className="bg-massive-logo-image"
          style={{
            filter: `drop-shadow(0 0 60px ${teamColor}) brightness(0.8) opacity(0.35)`,
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              "https://empireproleague.com/wp-content/uploads/2021/05/Empire-Logo-Official.png";
          }}
        />
      </div>

      <div className="bg-moving-grid"></div>
      <div className="bg-speed-lines"></div>
      <div className="bg-glow-pulse"></div>
      <div className="bg-vignette-heavy"></div>

      <div className="stage-content" key={currentTeam.id}>
        <div className="broadcast-badge">
          <div className="live-pulse"></div>
          <span className="badge-text">LIVE SELECTION</span>
        </div>

        <div className="team-text-wrapper">
          <h1
            className="team-name-display"
            style={{
              background: `linear-gradient(180deg, #fff 0%, ${teamColor} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: `drop-shadow(4px 4px 2px rgba(0,0,0,0.8))`,
            }}
          >
            {currentTeam.team_name}
          </h1>
        </div>

        <div className="bottom-info-strip">
          <div className="line-l" style={{ background: teamColor }}></div>
          <span className="on-the-clock-label">ON THE CLOCK</span>
          <div className="line-r" style={{ background: teamColor }}></div>
        </div>
      </div>

      <style>{`
        .empire-war-room-container {
          width: 100%;
          min-height: 140px; /* Reduced from 200px */
          background: ${empireGreen};
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          overflow: hidden;
          font-family: 'Oswald', sans-serif;
          border-top: 1px solid rgba(255,255,255,0.1);
          border-bottom: 4px solid #000;
          box-shadow: inset 0 0 80px rgba(0,0,0,0.7);
          margin-bottom: 20px;
          perspective: 2000px;
        }

        .bg-massive-logo-wrapper {
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          width: 50%;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          z-index: 1;
          pointer-events: none;
          transform: rotateY(15deg);
          transform-origin: left center;
        }

        .bg-massive-logo-image {
          height: 250%; /* Adjusted height */
          width: auto;
          object-fit: contain;
          margin-left: -5%;
          animation: logoDriftSubtle 20s ease-in-out infinite alternate;
        }

        .bg-static-texture {
          position: absolute;
          width: 100%; height: 100%;
          opacity: 0.1;
          background-image: url("https://www.transparenttextures.com/patterns/carbon-fibre.png");
        }

        .bg-moving-grid {
          position: absolute;
          width: 200%; height: 200%;
          background-image: linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
          background-size: 50px 50px;
          transform: perspective(500px) rotateX(70deg) translateY(-10%);
          animation: gridScroll 15s linear infinite;
          z-index: 2;
        }

        .bg-speed-lines {
          position: absolute;
          width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.04) 50%, transparent);
          background-size: 200% 100%;
          animation: speedSwipe 4s linear infinite;
          z-index: 2;
        }

        .bg-vignette-heavy {
          position: absolute;
          width: 100%; height: 100%;
          background: radial-gradient(circle, transparent 20%, rgba(0,0,0,0.85) 100%);
          z-index: 3;
        }

        .stage-content {
          position: relative;
          z-index: 10;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 10px 0;
          animation: impactSlam 0.6s cubic-bezier(0.19, 1, 0.22, 1);
        }

        .broadcast-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(0,0,0,0.7);
          padding: 2px 15px;
          border-radius: 4px;
          border-left: 3px solid #ff0000;
          margin-bottom: 5px;
        }

        .live-pulse {
          width: 8px; height: 8px;
          background: #ff0000;
          border-radius: 50%;
          box-shadow: 0 0 10px #ff0000;
          animation: blinker 0.8s infinite;
        }

        .badge-text {
          color: #fff;
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 3px;
          text-transform: uppercase;
        }

        .team-name-display {
          font-size: 2.8rem; /* Reduced from 6rem */
          font-weight: 900;
          text-transform: uppercase;
          margin: 5px 0;
          letter-spacing: 1px;
          line-height: 1;
        }

        .on-the-clock-label {
          color: #fff;
          font-size: 0.8rem; /* Reduced for balance */
          font-weight: 800;
          letter-spacing: 8px;
          text-transform: uppercase;
        }

        .bottom-info-strip {
          display: flex;
          align-items: center;
          gap: 15px;
          width: 100%;
        }

        .line-l, .line-r { height: 1px; flex-grow: 1; opacity: 0.5; }

        @keyframes logoDriftSubtle {
          0% { transform: translateY(0) translateX(0); }
          100% { transform: translateY(-2%) translateX(10px); }
        }

        @keyframes impactSlam {
          0% { transform: scale(2); opacity: 0; filter: brightness(3) blur(10px); }
          100% { transform: scale(1); opacity: 1; filter: brightness(1) blur(0); }
        }

        @keyframes gridScroll { from { background-position: 0 0; } to { background-position: 0 100px; } }
        @keyframes speedSwipe { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes blinker { 50% { opacity: 0.4; } }

        @media (max-width: 1024px) {
          .team-name-display { font-size: 2rem; }
          .bg-massive-logo-image { height: 180%; }
          .on-the-clock-label { font-size: 0.7rem; letter-spacing: 4px; }
        }
      `}</style>
    </div>
  );
};
